/** TasksPage — same shell as Decisions/Ideas. Reads public.tasks. */
import { useEffect, useMemo, useState } from "react";
import { ListTodo, Loader2, Filter } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { supabase } from "@/lib/supabaseClient";
import { useActiveProject } from "@/context/ActiveProjectContext";

const PRIORITY_TONE = {
    urgent: "text-red-300 bg-red-500/10",
    high: "text-amber-300 bg-amber-500/10",
    medium: "text-qoryn-cyan bg-qoryn-cyan/10",
    low: "text-qoryn-textDim bg-white/5",
};

export default function TasksPage() {
    const { projects } = useActiveProject();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [projectFilter, setProjectFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        supabase
            .from("tasks")
            .select("id, project_id, title, status, priority, source_type, source_session_id, created_at, completed_at")
            .order("created_at", { ascending: false })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) setError(error.message);
                else setRows(data ?? []);
            });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => (rows || []).filter((r) => projectFilter === "all" || r.project_id === projectFilter), [rows, projectFilter]);

    return (
        <div data-testid="tasks-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Tasks</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Concrete actions surfaced from conversations. Grouped by project + priority.
                </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 h-9 px-3 rounded-full qoryn-panel font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                    <Filter className="h-3 w-3" strokeWidth={1.75} />
                    Filter
                </div>
                <select
                    data-testid="tasks-project-filter"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-9 px-3 rounded-full qoryn-panel bg-transparent font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text qoryn-focus"
                >
                    <option value="all">All projects</option>
                    {projects.filter((p) => p.status !== "archived").map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>
            {rows === null && !error && (
                <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {error && (
                <EmptyState icon={ListTodo} title="Couldn't load tasks." body={error} />
            )}
            {rows !== null && !error && filtered.length === 0 && (
                <EmptyState
                    icon={ListTodo}
                    title="No tasks yet."
                    body="Action items land here after a voice session is summarized and promoted."
                />
            )}
            {filtered.length > 0 && (
                <ul data-testid="tasks-list" className="space-y-2">
                    {filtered.map((t) => (
                        <li key={t.id} data-testid={`task-row-${t.id}`} className="qoryn-panel rounded-2xl px-5 py-4 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                                <p className={`font-sans text-[14px] leading-relaxed flex-1 ${t.status === "done" ? "line-through text-qoryn-textMuted" : "text-qoryn-text"}`}>
                                    {t.title}
                                </p>
                                <div className="flex items-center gap-1.5">
                                    <span className={`inline-flex rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase ${PRIORITY_TONE[t.priority] || "bg-white/5 text-qoryn-textDim"}`}>
                                        {t.priority}
                                    </span>
                                    <span className="inline-flex rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim bg-white/5">
                                        {t.status}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <ProvenanceBadge sourceType={t.source_type} sourceSessionId={t.source_session_id} />
                                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                    {new Date(t.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
